import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { api, money, relativeTime } from '../lib/api';
import { Card, Empty, ErrorNotice, Loading, Stat, Status } from '../components/ui';

interface Detail {
  deviceId: string;
  locationName: string;
  status: string;
  online: boolean;
  lastCheckinAt: string | null;
  firmwareVersion: string | null;
  activeSessions: Array<{
    session_ref: string;
    traffic_group: string | null;
    status: string;
    started_at: string;
    expires_at: string | null;
    coupon_code: string | null;
  }>;
  revenue: { currency: string; invoicedTotal: number; invoiceCount: number } | null;
}

/** Wireframe B5 — device detail with revenue and active guest sessions. */
export function DeviceDetail() {
  const { deviceId = '' } = useParams();
  const detail = useApi<Detail>(`/devices/${deviceId}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function terminate(sessionRef: string) {
    setBusy(sessionRef);
    setError(null);
    try {
      await api.post(`/devices/${deviceId}/sessions/${sessionRef}/terminate`);
      detail.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  if (detail.loading) return <Loading />;
  if (detail.error) return <ErrorNotice error={detail.error} />;
  if (!detail.data) return <Empty title="Device not found" />;

  const d = detail.data;

  return (
    <>
      <div className="page-head">
        <h1>{d.locationName}</h1>
        <p>
          <span className="mono">{d.deviceId}</span> · <Status online={d.online} status={d.status} /> · firmware{' '}
          {d.firmwareVersion ?? 'unknown'} · checked in {relativeTime(d.lastCheckinAt)}
        </p>
      </div>

      <ErrorNotice error={error} />

      <div className="stats">
        <Stat value={money(d.revenue?.invoicedTotal, d.revenue?.currency)} label="Invoiced to date" />
        <Stat value={d.activeSessions.length} label="Active guest sessions" />
        <Stat value={d.revenue?.invoiceCount ?? 0} label="Invoices" />
      </div>

      <div className="actions" style={{ marginBottom: 16 }}>
        <Link className="btn" to={`/devices/${deviceId}/config`}>
          Configure
        </Link>
      </div>

      <Card title="Active guest sessions">
        {d.activeSessions.length === 0 ? (
          <Empty title="No guests connected">
            <p className="muted">Sessions appear here as guests come online.</p>
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Session</th>
                <th>Tier</th>
                <th>Started</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.activeSessions.map((session) => (
                <tr key={session.session_ref}>
                  <td className="mono">{session.session_ref}</td>
                  <td>{session.traffic_group ?? 'Free'}</td>
                  <td className="muted">{relativeTime(session.started_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="secondary small"
                      disabled={busy === session.session_ref}
                      onClick={() => terminate(session.session_ref)}
                    >
                      {busy === session.session_ref ? 'Ending…' : 'Terminate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
